/**
 * PromptLibraryWidget — Dashboard card showing the user's top-reused
 * prompts across every studio. Helps station owners see their creative
 * vocabulary build up over time and surfaces which ideas keep working.
 *
 * Lives in the right rail of the Control Room next to the other health
 * widgets. Polls `/api/prompts/favorites/top` on mount + station-switch.
 * Empty state guides the user to start starring suggestions.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Star, Sparkles, Loader2, Library } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SURFACE_LABELS = {
  dj:         "DJ",
  news:       "News",
  commercial: "Commercial",
  imaging:    "Imaging",
  podcast:    "Podcast",
  blog:       "Blog",
  academy:    "Academy",
};

const SURFACE_TINT = {
  dj:         "border-violet-500/40 bg-violet-500/10 text-violet-300",
  news:       "border-sky-500/40 bg-sky-500/10 text-sky-300",
  commercial: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  imaging:    "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  podcast:    "border-rose-500/40 bg-rose-500/10 text-rose-300",
  blog:       "border-indigo-500/40 bg-indigo-500/10 text-indigo-300",
  academy:    "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
};

export default function PromptLibraryWidget() {
  const [items, setItems] = useState([]);
  const [totalSaved, setTotalSaved] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prompts/favorites/top", { params: { limit: 10 } });
      setItems(data?.items || []);
      setTotalSaved(data?.total_saved || 0);
    } catch { /* anon or empty */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const onSwitch = () => refresh();
    window.addEventListener("station-switched", onSwitch);
    return () => window.removeEventListener("station-switched", onSwitch);
  }, [refresh]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied — paste anywhere");
    } catch { toast.error("Couldn't copy"); }
  };

  return (
    <div
      className="rounded-xl border border-line bg-bg-surface p-4"
      data-testid="prompt-library-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-amber-400/15 text-amber-300 flex items-center justify-center">
            <Library className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-amber-300">// Prompt Library</div>
            <div className="text-[10px] text-ink-muted -mt-0.5">
              {totalSaved === 0 ? "Nothing saved yet" : `${totalSaved} saved · top ${items.length} reused`}
            </div>
          </div>
        </div>
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-muted" />}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-6 px-2" data-testid="prompt-library-empty">
          <div className="h-10 w-10 mx-auto rounded-full bg-amber-400/10 text-amber-300 flex items-center justify-center mb-2">
            <Star className="h-4 w-4" />
          </div>
          <div className="text-xs text-ink-primary font-medium">No reused prompts yet.</div>
          <p className="text-[11px] text-ink-muted mt-1 max-w-[16rem] mx-auto leading-relaxed">
            Click the <Sparkles className="inline h-3 w-3 align-text-bottom text-violet-300" /> AI Suggest
            chip on any field, then star a suggestion to save it here.
          </p>
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="prompt-library-list">
          {items.map((f) => (
            <li key={f.fav_id}>
              <button
                type="button"
                onClick={() => copyToClipboard(f.text)}
                data-testid={`prompt-library-${f.fav_id}`}
                title="Click to copy"
                className="w-full text-left rounded-md border border-line bg-bg-elevated/40 px-2.5 py-1.5 hover:border-amber-400/40 hover:bg-amber-400/5 transition group"
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span
                    className={`text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                      SURFACE_TINT[f.surface] || "border-line text-ink-muted"
                    }`}
                  >
                    {SURFACE_LABELS[f.surface] || f.surface}
                  </span>
                  <span className="text-[9px] font-mono text-ink-muted uppercase">{f.field.replace(/_/g, " ")}</span>
                  <span className="ml-auto text-[9px] font-mono text-amber-300/70">
                    {f.used_count}×
                  </span>
                </div>
                <div className="text-[11px] text-ink-primary line-clamp-2 leading-snug">
                  {f.text}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
